import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AcInvestmentTypes } from 'components/templates/AcInvestmentRecommendation';
import { getIndexQuotesService } from 'services/AcDashboard';
import { IndexQuotesDataTypes, IndexQuotesSocketDataTypes } from 'services/AcDashboard/types';
import getMarketShareService, {
  getStockExchangesListService,
  getRecommendationCategoryService,
  getRecommendationByCateCodeService,
  getAnalyticsCompaniesBySlugService,
  getNewsListDashboardService,
} from 'services/analyticsCenter';
import {
  MarketShareDataTypes,
  MarketShareParamsTypes,
  StockExchangesListTypes,
  AnalyticCenterCompaniesTypes,
  RecommendationTableTypes,
  NewsListTypes,
  ParamsTypes,
} from 'services/analyticsCenter/types';
import getWsPriceboardService from 'services/wsPriceboard';
import { parseStringToNumber, returnIndexQuotes } from 'utils/functions';

interface StockExchangesState {
  dataStockExchange: StockExchangesListTypes[];
  dataMarketShare?: MarketShareDataTypes;
  analyticCenter?: AnalyticCenterCompaniesTypes;
  dataRecommendation: AcInvestmentTypes[];
  newsDashboard: NewsListTypes[];
  indexQuotesSocketData: IndexQuotesSocketDataTypes[];
  indexQuotesData: IndexQuotesDataTypes[];
}

const initialState: StockExchangesState = {
  dataStockExchange: [],
  dataMarketShare: undefined,
  analyticCenter: undefined,
  dataRecommendation: [],
  newsDashboard: [],
  indexQuotesSocketData: [],
  indexQuotesData: [],
};

export const getStockExchangesListAction = createAsyncThunk<
  StockExchangesListTypes[],
  void,
  { rejectValue: ErrorResponse }
>(
  'analyticsCenterReducer/getStockExchangesList',
  async (_, { rejectWithValue }) => {
    try {
      return await getStockExchangesListService();
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getMarketShareAction = createAsyncThunk<
  MarketShareDataTypes,
  MarketShareParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'analyticsCenterReducer/getMarketShare',
  async (params: MarketShareParamsTypes, { rejectWithValue }) => {
    try {
      const response = await getMarketShareService(params);
      return response;
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getAnalyticsCenterAction = createAsyncThunk<
  AnalyticCenterCompaniesTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'analysticsCenterReducer/getAnalysticsCenter',
  async (slug, { rejectWithValue }) => {
    try {
      return await getAnalyticsCompaniesBySlugService(slug);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);
export const getRecomendationCateListAction = createAsyncThunk<
  AcInvestmentTypes[],
  void,
  { rejectValue: ErrorResponse }
>(
  'analyticsCenterReducer/getRecomemendationCateList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRecommendationCategoryService();

      if (response.length > 0) {
        const dataFirstTab = await getRecommendationByCateCodeService({
          category_code: response[0].code,
        });
        return response.map((item) => ({
          title: item.name,
          code: item.code,
          dataHeader:
            dataFirstTab.category.code === item.code
              ? Object.entries(dataFirstTab.columnsMapping).map(([k, v]) => ({
                id: k,
                value: v,
              }))
              : [],
          dataTable: [...dataFirstTab.columnsData],
        }));
      }
      return [];
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getRecomendationCateByCodeAction = createAsyncThunk<
  RecommendationTableTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'economicInformationReducer/getRecomendationCateByCode',
  async (cateCode: string, { rejectWithValue }) => {
    try {
      const res = await getRecommendationByCateCodeService({
        category_code: cateCode,
      });
      return res;
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getNewsDashboardListAction = createAsyncThunk<
  NewsListTypes[],
  ParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'analyticsCenterReducer/getNewsDashboardList',
  async (params: ParamsTypes, { rejectWithValue }) => {
    try {
      const response = await getNewsListDashboardService(params);
      return response;
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

// export const getIndexQuotesSocketAction = createAsyncThunk<
// IndexQuotesSocketDataTypes[],
//   void,
//   { rejectValue: ErrorResponse }
// >('acDashboardReducer/getIndexQuotesSocketAction', async (_, { rejectWithValue }) => {
//   try {
//     return await getIndexQuotesSocketService();
//   } catch (error) {
//     return rejectWithValue(error as ErrorResponse);
//   }
// });

export const getIndexQuotesSocketAction = createAsyncThunk<
IndexQuotesSocketDataTypes[],
  void,
  { rejectValue: ErrorResponse }
>('acDashboardReducer/getIndexQuotesSocketAction', async (_, { rejectWithValue }) => {
  try {
    const dataMapping: IndexQuotesSocketDataTypes[] = [];
    const response = await getWsPriceboardService({
      selectedStocks: 'VCB',
      criteriaId: '',
      marketId: 0,
      lastSeq: 0,
      isReqTL: false,
      isReqMK: false,
      tlSymbol: 'VCB',
      pthMktId: '',
      isGetNullValue: false,
      isIncrValue: true,
    });
    for (let i = 0; i < response.mi.length; i += 1) {
      const element = response.mi[i];
      if (element && element[0] && element[0].e) {
        dataMapping.push({
          exchangeCode: returnIndexQuotes(element[0].a),
          value: parseStringToNumber(element[0].e),
          changeValue: parseStringToNumber(element[0].f),
          changeValuePercent: parseStringToNumber(element[0].g),
          updatedAt: '',
        });
      }
    }
    return dataMapping;
  } catch (error) {
    return rejectWithValue(error as ErrorResponse);
  }
});

export const getIndexQuotesAction = createAsyncThunk<
IndexQuotesDataTypes[],
  void,
  { rejectValue: ErrorResponse }
>('acDashboardReducer/getIndexQuotesAction', async (_, { rejectWithValue }) => {
  try {
    return await getIndexQuotesService();
  } catch (error) {
    return rejectWithValue(error as ErrorResponse);
  }
});

const analyticsCenterSlice = createSlice({
  name: 'analyticsCenterReducer',
  initialState,
  reducers: {
    socketUpdateIndexQuote: (
      $state,
      action: PayloadAction<{ data: IndexQuotesSocketDataTypes; index: number }>,
    ) => {
      const copy = $state.indexQuotesSocketData.slice();
      copy[action.payload.index] = action.payload.data;
      $state.indexQuotesSocketData = copy;
    },
  },
  extraReducers(builder) {
    builder.addCase(getStockExchangesListAction.fulfilled, ($state, action) => {
      $state.dataStockExchange = action.payload;
    });
    builder.addCase(getMarketShareAction.fulfilled, ($state, action) => {
      $state.dataMarketShare = action.payload;
    });
    builder.addCase(getAnalyticsCenterAction.fulfilled, ($state, action) => {
      $state.analyticCenter = action.payload;
    });
    builder.addCase(
      getRecomendationCateListAction.fulfilled,
      ($state, action) => {
        $state.dataRecommendation = action.payload;
      },
    );
    builder.addCase(
      getRecomendationCateByCodeAction.fulfilled,
      ($state, action) => {
        const baseData = JSON.parse(JSON.stringify($state.dataRecommendation));
        const findData = baseData.find(
          (item: AcInvestmentTypes) => item.code === action.payload.category.code,
        );
        const foundIndex = baseData.findIndex(
          (x: AcInvestmentTypes) => x.code === action.payload.category.code,
        );
        const newData = {
          ...findData,
          dataHeader: Object.entries(
            action.payload.columnsMapping,
          ).map(([k, v]) => ({ id: k, value: v })),
          dataTable: [...action.payload.columnsData],
        };
        baseData[foundIndex] = newData;
        $state.dataRecommendation = baseData;
      },
    );
    builder.addCase(getNewsDashboardListAction.fulfilled, ($state, action) => {
      $state.newsDashboard = action.payload;
    });
    builder.addCase(getIndexQuotesSocketAction.fulfilled, ($state, action) => {
      $state.indexQuotesSocketData = action.payload;
    });
    builder.addCase(getIndexQuotesAction.fulfilled, ($state, action) => {
      $state.indexQuotesData = action.payload;
    });
  },
});

export const { socketUpdateIndexQuote } = analyticsCenterSlice.actions;

export default analyticsCenterSlice.reducer;
